import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext({ user: null, loading: true, login: async () => {}, logout: async () => {} });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem("admin_token") || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (token) api.defaults.headers.common.Authorization = `Bearer ${token}`;
    else delete api.defaults.headers.common.Authorization;
  }, [token]);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only probe /auth/me on admin routes or when a token is already present.
    // This keeps public marketing pages free of 401 console noise.
    const isAdminRoute = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
    if (isAdminRoute || token) {
      refreshMe();
    } else {
      setLoading(false);
    }
  }, [refreshMe, token]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    if (data.access_token) {
      setToken(data.access_token);
      try {
        localStorage.setItem("admin_token", data.access_token);
      } catch {
        // ignore
      }
    }
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    setUser(null);
    setToken(null);
    try {
      localStorage.removeItem("admin_token");
    } catch {
      // ignore
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshMe }}>{children}</AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
